import { Injectable, NgZone, OnDestroy } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { BehaviorSubject, Observable, Subject } from 'rxjs';
import { filter, map, take, takeUntil } from 'rxjs/operators';

import { UserNotify } from '../base-models/user-notify';
import { ConfigService } from './config.service';
import { BaseDataService } from './base-data.service';
import { QueryParameter } from '../base-models/query-parameter';
import { CryptoService } from './crypto.service';

declare var $: any;

type FwEntityChanged = { entity: string; id: number; meta?: any; view?: any };

@Injectable({ providedIn: 'root' })
export class SignalRService implements OnDestroy {
  private hubConnection: any;
  private hubProxy: any;

  private unsubscribe$ = new Subject<void>();

  private connected$ = new BehaviorSubject<boolean>(false);

  private wantedGroups = new Set<string>();

  private currentUserIdForConnection: string | null = null;

  private startInFlight: Promise<any> | null = null;
  private retryAttempt = 0;
  private reconnectEnabled = true;
  private reconnectTimer: any = null;

  private notificationSubject = new Subject<{
    Poruka: string;
    Sadrzaj: string;
    DatumIVremePoruke: Date;
    Procitano: boolean;
  }>();
  public notifications$ = this.notificationSubject.asObservable();

  private fwEntityChangedSubject = new Subject<FwEntityChanged>();
  public fwEntityChanged$ = this.fwEntityChangedSubject.asObservable();

  constructor(
    private http: HttpClient,
    private configService: ConfigService,
    protected baseDataService: BaseDataService,
    private ngZone: NgZone
  ) {}

  public entityChanged$(entity: string): Observable<number> {
    return this.fwEntityChanged$.pipe(
      filter(m => !!m && m.entity === entity && !!m.id),
      map(m => m.id)
    );
  }

  public whenConnectedOnce(): Observable<boolean> {
    return this.connected$.asObservable().pipe(filter(x => x), take(1));
  }

  public joinEntity(entity: string, id: number) {
    const key = `${entity}:${id}`;
    this.wantedGroups.add(key);
    if (!this.hasSignalRLib() || !this.hubProxy) return Promise.resolve();
    return this.hubProxy.invoke('JoinEntity', entity, id)
      // .done(() => console.log('JOIN ok', key))
      // .fail((err: any) => console.error('JOIN fail', key, err));
  }

  public leaveEntity(entity: string, id: number) {
    const key = `${entity}:${id}`;
    this.wantedGroups.delete(key);
    if (!this.hasSignalRLib() || !this.hubProxy) return Promise.resolve();
    return this.hubProxy.invoke('LeaveEntity', entity, id)
      // .done(() => console.log('LEAVE ok', key))
      // .fail((err: any) => console.error('LEAVE fail', key, err));
  }

  public startConnection(userId: string): Observable<boolean> {
    const status$ = new Subject<boolean>();

    if (!this.hasSignalRLib()) {
      status$.next(false);
      status$.complete();
      return status$.asObservable();
    }

    this.configService.GetApiUrlAsync()
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe((baseUrl: string) => {
        const signalRUrl = baseUrl + 'signalr';

        if (!this.hasSignalRLib()) {
          status$.next(false);
          status$.complete();
          return;
        }

        if (this.hubConnection &&
            (this.hubConnection.state === $.signalR.connectionState.connected ||
             this.hubConnection.state === $.signalR.connectionState.connecting)) {

          if (this.currentUserIdForConnection !== userId) {
            this.stopConnection();
          } else {
            status$.next(true);
            status$.complete();
            return;
          }
        }

        if (this.startInFlight) {
          this.startInFlight.then(() => { status$.next(true);  status$.complete(); })
                            .catch(() => { status$.next(false); status$.complete(); });
          return;
        }

        this.reconnectEnabled = true;
        if (this.reconnectTimer) { clearTimeout(this.reconnectTimer); this.reconnectTimer = null; }

        this.hubConnection = $.hubConnection(signalRUrl, { qs: { userId: userId } });
        this.currentUserIdForConnection = userId;
        this.hubConnection.logging = false;
        this.hubProxy = this.hubConnection.createHubProxy('notificationHub');
        this.registerHubHandlers();

        this.startInFlight = this.hubConnection.start()
          .done(() => {
            this.retryAttempt = 0;
            this.connected$.next(true);
            this.deleteReadNotifications(+userId);
            this.flushJoins();
            status$.next(true);
            status$.complete();
          })
          .fail((_err: any) => {
            this.connected$.next(false);
            status$.next(false);
            status$.complete();
          })
          .always(() => {
            this.startInFlight = null;
          });

        this.hubConnection.reconnecting(() => { });
        this.hubConnection.reconnected(() => {
          this.retryAttempt = 0;
          this.connected$.next(true);
          this.flushJoins();
        });
        this.hubConnection.disconnected(() => {
          this.connected$.next(false);
          if (this.reconnectEnabled && this.currentUserIdForConnection) {
            this.scheduleReconnect(this.currentUserIdForConnection);
          }
        });

      }, () => { status$.next(false); status$.complete(); });

    return status$.asObservable();
  }

  public loadNotifications(userId: string): void {
    const parameters = [new QueryParameter('userId', userId)];
    this.baseDataService
      .Get('usernotify', 'GetByUserID', parameters, UserNotify)
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe((notifications: any[]) => {
        (notifications || []).forEach(n => this.notificationSubject.next(n));
      });
  }

  public markAsRead(notification: any): void {
    const parameters = {
      ID: notification.ID,
      Procitano: notification.Procitano,
      UserId: notification.UserId,
      DatumIVremePoruke: notification.DatumIVremePoruke,
      Poruka: notification.Poruka,
      Sadrzaj: notification.Sadrzaj,
      VremeProcitanaPoruka: notification.VremeProcitanaPoruka
    };

    this.baseDataService
      .Post('UserNotify', 'MarkAsRead', parameters, null)
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe();
  }

  public deleteReadNotifications(userId: number): void {
    this.baseDataService
      .Post('UserNotify', 'DeleteRead', userId, null)
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe();
  }


  private registerHubHandlers(): void {
    if (!this.hubProxy) return;

    if (this.hubProxy.off) {
      this.hubProxy.off('receiveNotification');
      this.hubProxy.off('fwEntityChanged');
    }

    this.hubProxy.on('receiveNotification', (data: any) => {
      this.ngZone.run(() => {
        const currentUserId = this.getCurrentUserId();
        if (currentUserId && data.UserId === currentUserId) {
          this.notificationSubject.next(data);
        }
      });
    });

    this.hubProxy.on('fwEntityChanged', (payload: FwEntityChanged) => {
      this.ngZone.run(() => {
        if (payload && payload.entity && payload.id) {
          this.fwEntityChangedSubject.next(payload);
        }
      });
    });
  }

  private flushJoins(): void {
    if (!this.hubProxy) return;
    this.wantedGroups.forEach(key => {
      const parts = key.split(':');
      const entity = parts[0];
      const id = +parts[1];
      if (entity && id) {
        try {
          this.hubProxy.invoke('JoinEntity', entity, id)
            // .done(() => console.log('JOIN ok', key))
            // .fail((err: any) => console.error('JOIN fail', key, err));
        } 
        catch (e) { console.error('JOIN ex', key, e); }
      }
    });
  }

  private getCurrentUserId(): number {
    const raw = sessionStorage.getItem('sel-user');
    if (!raw) return 0;
    const decrypted = CryptoService.Decrypt(raw);
    const val = (decrypted && (decrypted as any).UserID);
    return typeof val === 'number' ? val : +val || 0;
  }

  public stopConnection(): void {
    this.reconnectEnabled = false;
    try { if (this.hubConnection) this.hubConnection.stop(); } catch {}
    if (this.reconnectTimer) { clearTimeout(this.reconnectTimer); this.reconnectTimer = null; }

    this.connected$.next(false);
    this.startInFlight = null;
    this.retryAttempt = 0;
    this.hubProxy = null as any;
    this.hubConnection = null as any;
    this.currentUserIdForConnection = null;
  }

  ngOnDestroy(): void {
    this.unsubscribe$.next();
    this.unsubscribe$.complete();
    this.stopConnection();
  }

  private scheduleReconnect(userId?: string) {
    if (!this.hasSignalRLib()) return;

    const effectiveUserId = userId ?? this.currentUserIdForConnection;
    if (!this.reconnectEnabled || !effectiveUserId || this.currentUserIdForConnection !== effectiveUserId) return;

    if (this.reconnectTimer) { clearTimeout(this.reconnectTimer); this.reconnectTimer = null; }

    this.retryAttempt = Math.min(this.retryAttempt + 1, 6);
    const base = 1000 * Math.pow(2, this.retryAttempt);
    const jitter = Math.floor(Math.random() * 500);

    this.reconnectTimer = setTimeout(() => {
      if (!this.reconnectEnabled || this.currentUserIdForConnection !== effectiveUserId) return;
      this.startConnection(effectiveUserId).subscribe();
    }, base + jitter);
  }

  private hasSignalRLib(): boolean {
    return (typeof $ !== 'undefined') && !!($.hubConnection) && !!($.signalR);
  }
}
